import { useEffect, useState } from "react";
import { Typography, Grid, Card, CardContent, Checkbox, SelectField, Option, TextField, Link, Button, CircularProgress } from "@deere/fuel-react";
import { GenericFuelModal } from "../../../../components/modals/GenericFuelModal";
import { getReportConfigurationPreview } from "../../../../services/survey.service";
import { useOktaAuth } from "@okta/okta-react";
import SelfServiceButtons from "../SelfServiceButtons";
import SelfServiceLoader from "../SelfServiceLoader";
import { getFiltersCatalog } from "../../../../services/userService";
import { validateReportConfiguration } from "../../../../utils/validation";
import { SAVING_DATA, SURVEYPART_SECTION_TYPES, SELF_SERVICE_QUE_TYPES_GENERIC, SELF_SERVICE_QUE_TYPES_PART, DEFAULT_PART_SECTION_TITLES, REPORT_EXCLUDED_QUESTION_TYPES } from "../../../../utils/surveyLiterals";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Chip from "@mui/material/Chip";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

const ReportConfigurationStep = ({reportConfigurationError, setReportConfigurationError, currentStep, activeStep, setActiveStep, payloadData, setPayloadData, languageList }) => {
    const [loader, setLoader] = useState({ isLoading: false, message: "" });
    const [filtersCatalogMap, setFiltersCatalogMap] = useState(new Map());
    const { authState } = useOktaAuth();
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(undefined);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState("");
    const [expandedSections, setExpandedSections] = useState({});

    const supportedFilterQuestionTypes = new Set(["OPEN", "CHECK", "NUM", "SELECT"]);
    const OPTION_OPERATOR_LABELS = { EQ: "Is Any Of", NEQ: "Is Not Any Of" };

    const questionTypeLabelMap = [...SELF_SERVICE_QUE_TYPES_GENERIC, ...SELF_SERVICE_QUE_TYPES_PART].reduce((acc, questionType) => {
        acc[questionType.code] = questionType.type;
        return acc;
    }, {});

    const questionTypeToFilterTypeMap = {
        OPEN_SMALL: "OPEN",
        OPEN: "OPEN",
        NUM: "NUM",
        CHECK: "CHECK",
        SELECT: "SELECT",
        RADIO: "SELECT",
        MULTI_SEL: "SELECT",
        SEL_CNTRY: "SELECT",
        SEL_CTY_UW: "SELECT",
        SEL_STATE: "SELECT",
    };

    const questionLabelTypeToFilterTypeMap = {
        "Text (Open)": "OPEN",
        "Open Text": "OPEN",
        "Open Multiline": "OPEN",
        "Numerical": "NUM",
        "Checkbox": "CHECK",
        "Option": "SELECT",
        "Country": "SELECT",
    };

    const resolveFilterQuestionType = (questionFilter) => {
        if (questionFilter?.filterQuestionType) return questionFilter.filterQuestionType;
        if (questionFilter?.questionType && questionTypeToFilterTypeMap[questionFilter.questionType]) {
            return questionTypeToFilterTypeMap[questionFilter.questionType];
        }
        if (questionFilter?.type && questionLabelTypeToFilterTypeMap[questionFilter.type]) {
            return questionLabelTypeToFilterTypeMap[questionFilter.type];
        }
        return "";
    };

    const findExistingFilters = (question, existingQuestionFilters) => {
        const questionCode = String(question?.code || "").trim();
        const questionId = question?.id;

        return existingQuestionFilters.filter((filter) => {
            const filterKey = String(filter?.key || "").trim();
            const filterQuestionCode = String(filter?.questionCode || "").trim();
            const filterQuestionId = filter?.questionId;

            const matchesByCode = questionCode !== "" && (filterKey === questionCode || filterQuestionCode === questionCode);
            const matchesById = questionId !== undefined && questionId !== null
                && filterQuestionId !== undefined && filterQuestionId !== null
                && String(filterQuestionId) === String(questionId);

            return matchesByCode || matchesById;
        });
    };

    const resolveSelectValuesForUi = (existingFilters = [], selectionOptions = []) => {
        const resolvedValues = existingFilters.flatMap((existingFilter) => {
            const savedValues = Array.isArray(existingFilter?.value) ? existingFilter.value : [existingFilter?.value];
            return savedValues.map((savedValue) => {
                const currentValue = String(savedValue ?? "").trim();
                if (currentValue === "") return "";

                const byDescription = selectionOptions.find((option) => String(option.description) === currentValue);
                if (byDescription) return byDescription.description;

                const byCode = selectionOptions.find((option) => existingFilter?.selectionCode && option.code && String(option.code) === String(existingFilter.selectionCode));
                if (byCode) return byCode.description;

                const byValue = selectionOptions.find((option) => String(option.value) === currentValue);
                if (byValue) return byValue.description;

                return currentValue;
            });
        });

        return [...new Set(resolvedValues.filter((value) => value !== ""))];
    };

    const buildSelectionOption = (questionCode, selection, fallbackIndex) => {
        const stableIdentifier =
            selection?.id
            ?? selection?.code
            ?? selection?.value
            ?? selection?.text
            ?? selection?.showOrder
            ?? `${questionCode}_${fallbackIndex}`;

        return {
            id: String(stableIdentifier),
            code: selection?.code || String(selection?.value ?? selection?.text ?? ""),
            value: String(selection?.value ?? selection?.code ?? selection?.text ?? ""),
            description: selection?.text || selection?.code || String(selection?.value || ""),
        };
    };

    const buildQuestionFiltersFromPartQuestions = (sections, existingQuestionFilters) =>
        (sections || [])
        .filter((section) => section.type === SURVEYPART_SECTION_TYPES.PART)
        .sort((a, b) => (a.showOrder ?? 0) - (b.showOrder ?? 0))
        .flatMap((section) =>
            (section.questions || [])
                .sort((a, b) => (a.showOrder ?? 0) - (b.showOrder ?? 0))
                .filter((question) => question?.code)
                .map((question) => {
                    const existingFilters = findExistingFilters(question, existingQuestionFilters);
                    const existingFilter = existingFilters[0];
                    const filterQuestionType = questionTypeToFilterTypeMap[question.type] || resolveFilterQuestionType(existingFilter);
                    if (!supportedFilterQuestionTypes.has(filterQuestionType)) return null;

                    const selectionOptions = filterQuestionType === "SELECT"
                        ? (question.selections || []).map((selection, index) => buildSelectionOption(question.code, selection, index))
                        : [];

                    return {
                        key: question.code,
                        sectionCode: section.code,
                        sectionShowOrder: section.showOrder,
                        sectionDescription: section.description || section.code,
                        questionCode: existingFilter?.questionCode || question.code,
                        questionId: existingFilter?.questionId ?? question.id ?? null,
                        questionText: question.text?.trim() || existingFilter?.questionText || question.code,
                        label: question.text && question.text.trim() !== "" ? question.text : question.code,
                        type: questionTypeLabelMap[question.type] || question.type || "Question",
                        questionType: filterQuestionType,
                        filterQuestionType: filterQuestionType || "",
                        enabled: existingFilter ? (existingFilter.enabled ?? true) : false,
                        operatorCode: existingFilter?.operatorCode || "",
                        operatorId: existingFilter?.operatorId || null,
                        value: filterQuestionType === "SELECT"
                            ? resolveSelectValuesForUi(existingFilters, selectionOptions)
                            : (existingFilter?.value ?? ""),
                        selectionOptions,
                    };
                })
                .filter(Boolean)
        );

    // Functions for building and comparing sections and questions to include in the report configuration
    const buildSectionColumns = (section) => {
        const additionalColumns = (payloadData?.defaultColumns || [])
            .filter((col) => Boolean(col.isNonDeere) === Boolean(payloadData?.nonDeereUse) && col.defaultColumnMapping && col.columnNames)
            .map((col) => ({
                key: `col_${col.defaultColumnMapping}`,
                label: col.columnNames,
                columnMapping: col.defaultColumnMapping,
            }));

        const questionColumns = (section?.questions || [])
            .filter((question) => question.active !== false && !question.html && !REPORT_EXCLUDED_QUESTION_TYPES.includes(question.type))
            .sort((a, b) => (a.showOrder ?? 0) - (b.showOrder ?? 0))
            .filter((question) => question?.code)
            .map((question) => ({
                key: `que_${question.code}`,
                label: question.text?.trim() || question.code,
                questionId: question.id ?? null,
                questionCode: question.code,
                questionText: question.text,
                questionType: question.type,
            }));

        return [...additionalColumns, ...questionColumns];
    };

    // Functions for comparing questions and sections to determine if they are equal
    const matchesColumn = (questionToInclude, column) =>
        column.columnMapping
            ? String(questionToInclude?.columnMapping || "") === String(column.columnMapping)
            : String(questionToInclude?.questionCode || "") === String(column.questionCode);

    const buildQuestionsToInclude = (section, existingQuestionsToInclude = []) =>
        buildSectionColumns(section).map((column) => {
            const existing = existingQuestionsToInclude.find((questionToInclude) => matchesColumn(questionToInclude, column));
            return {
                ...(existing?.id != null ? { id: existing.id } : {}),
                ...(column.columnMapping
                    ? { columnMapping: column.columnMapping }
                    : {
                        questionId: column.questionId,
                        questionCode: column.questionCode,
                        questionText: column.questionText,
                        questionType: column.questionType,
                    }),
                reportEnabled: existing ? Boolean(existing.reportEnabled) : true,
                ...(column.questionType === "MULTI_SEL" ? { columnToRow: Boolean(existing?.columnToRow) } : {}),
            };
        });

    const areQuestionsToIncludeEqual = (leftQuestions = [], rightQuestions = []) => {
        if ((leftQuestions?.length || 0) !== (rightQuestions?.length || 0)) return false;
        return (leftQuestions || []).every((leftQuestion, index) => {
            const rightQuestion = (rightQuestions || [])[index];
            return String(leftQuestion.questionCode ?? "") === String(rightQuestion.questionCode ?? "")
                && String(leftQuestion.columnMapping ?? "") === String(rightQuestion.columnMapping ?? "")
                && Boolean(leftQuestion.reportEnabled) === Boolean(rightQuestion.reportEnabled)
                && Boolean(leftQuestion.columnToRow) === Boolean(rightQuestion.columnToRow);
        });
    };

    const buildSectionsToInclude = (sections, existingSectionsToInclude = []) => {
        const builtSections = (sections || [])
        .filter((section) => section?.code && section.type !== SURVEYPART_SECTION_TYPES.RESPONSES)
        .map((section) => {
            const existing = existingSectionsToInclude.find((s) =>
                (s.sectionId != null && section.id != null && String(s.sectionId) === String(section.id))
                || (s.code && section.code && String(s.code) === String(section.code))
            );
        return {
            ...(existing?.id != null ? { id: existing.id } : {}),
            sectionId: section.id ?? existing?.sectionId ?? null,
            code: section.code,
            description: section.description || section.code,
            type: section.type,
            active: existing?.active ?? true,
            reportEnabled: existing ? Boolean(existing.reportEnabled) : Boolean(section.isDefault),
            signatureSectionEnabled: existing ? Boolean(existing.signatureSectionEnabled) : false,
            ...(section.type === SURVEYPART_SECTION_TYPES.PART ? { questionsToInclude: buildQuestionsToInclude(section, existing?.questionsToInclude || []) } : {}),
        };
        });

        let keptRowPerSelectedItem = false;
        return builtSections.map((section) => ({
            ...section,
            ...(section.questionsToInclude ? {
                questionsToInclude: section.questionsToInclude.map((questionToInclude) => {
                    if (questionToInclude.questionType !== "MULTI_SEL") return questionToInclude;
                    const keepEnabled = questionToInclude.questionType === "MULTI_SEL"
                        && questionToInclude.columnToRow
                        && !keptRowPerSelectedItem;
                    if (keepEnabled) keptRowPerSelectedItem = true;
                    return { ...questionToInclude, columnToRow: keepEnabled };
                }),
            } : {}),
        }));
    };

   const areSectionsToIncludeEqual = (leftSections = [], rightSections = []) => {
        if (leftSections.length !== rightSections.length) return false;
        return leftSections.every((leftSection, index) => {
            const rightSection = rightSections[index];
            return leftSection.code === rightSection.code
                && String(leftSection.sectionId) === String(rightSection.sectionId)
                && leftSection.description === rightSection.description
                && leftSection.type === rightSection.type
                && Boolean(leftSection.reportEnabled) === Boolean(rightSection.reportEnabled)
                && Boolean(leftSection.signatureSectionEnabled) === Boolean(rightSection.signatureSectionEnabled)
                && areQuestionsToIncludeEqual(leftSection.questionsToInclude, rightSection.questionsToInclude);
        });
    };

   const areQuestionFiltersEqual = (leftFilters = [], rightFilters = []) => {
        if (leftFilters.length !== rightFilters.length) return false;
        return leftFilters.every((leftFilter, index) => {
            const rightFilter = rightFilters[index];
            return leftFilter.key === rightFilter.key
                && leftFilter.label === rightFilter.label
                && leftFilter.questionText === rightFilter.questionText
                && leftFilter.type === rightFilter.type
                && leftFilter.questionType === rightFilter.questionType
                && leftFilter.filterQuestionType === rightFilter.filterQuestionType
                && Boolean(leftFilter.enabled) === Boolean(rightFilter.enabled);
        });
    };

    const onDisclaimerMessageChange = (value) => {
        updateReportConfiguration((currentConfig) => ({
            ...currentConfig,
            disclaimerMessage: value,
        }));
    };

    const onAdditionalElementToggle = (elementKey, checked) => {
        updateReportConfiguration((currentConfig) => ({
            ...currentConfig,
            [elementKey]: checked,
            ...(elementKey === "includeDisclaimerSection" && !checked ? { disclaimerMessage: "" } : {}),
            ...(elementKey === "includeSignatureSection" && !checked ? { sectionsToInclude: (currentConfig.sectionsToInclude || []).map((s) => ({ ...s, signatureSectionEnabled: false })) } : {}),
        }));
    };

    useEffect(() => {
        if (!authState?.accessToken?.accessToken) return;

        getFiltersCatalog(authState.accessToken.accessToken)
            .then((filtersMap) => {
                console.log("filters catalog loaded", Array.from(filtersMap.entries()));
                setFiltersCatalogMap(filtersMap);
            })
            .catch((error) => {
                console.log(error);
                setFiltersCatalogMap(new Map());
            });
    }, [authState?.accessToken?.accessToken]);

    useEffect(() => {
        const existingQuestionFilters = payloadData?.reportConfiguration?.questionFilters || [];
        const generatedQuestionFilters = buildQuestionFiltersFromPartQuestions(payloadData?.sections, existingQuestionFilters);
        if (generatedQuestionFilters.length === 0 || areQuestionFiltersEqual(existingQuestionFilters, generatedQuestionFilters)) return;

        setPayloadData((prevState) => ({
            ...prevState,
            reportConfiguration: {
                ...(prevState.reportConfiguration || {}),
                questionFilters: generatedQuestionFilters,
            },
        }));
    }, [payloadData?.sections, payloadData?.reportConfiguration?.questionFilters]);

    useEffect(() => {
        const existing = payloadData?.reportConfiguration?.sectionsToInclude || [];
        const generated = buildSectionsToInclude(payloadData?.sections, existing);
        if (generated.length === 0 || areSectionsToIncludeEqual(existing, generated)) return;

        setPayloadData((prevState) => ({
            ...prevState,
            reportConfiguration: {
                ...(prevState.reportConfiguration || {}),
                sectionsToInclude: generated,
            },
        }));
    }, [payloadData?.sections, payloadData?.reportConfiguration?.sectionsToInclude]);
    
    useEffect(() => {
        if (reportConfigurationError?.show) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [reportConfigurationError?.show]);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                window.URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    useEffect(() => {
        setPreviewUrl(undefined);
    }, [payloadData?.reportConfiguration]);

    const payloadReportConfig = payloadData?.reportConfiguration || {};
    const reportConfig = {
        enableReportGeneration: Boolean(payloadReportConfig.enableReportGeneration),
        includeSignatureSection: payloadReportConfig.includeSignatureSection ?? Boolean(payloadReportConfig?.additionalElements?.signatureSection),
        includeDisclaimerSection: payloadReportConfig.includeDisclaimerSection ?? Boolean(payloadReportConfig?.additionalElements?.disclaimerAgreement),
        disclaimerMessage: payloadReportConfig.disclaimerMessage ?? "",
        questionFilters: payloadReportConfig?.questionFilters?.length > 0
            ? payloadReportConfig.questionFilters
            : [],
        sectionsToInclude: payloadReportConfig?.sectionsToInclude || [],
        ...payloadReportConfig,
    };

    const sectionsToInclude = reportConfig.sectionsToInclude || [];

    const isSectionReportEnabled = (sectionCode) => Boolean(sectionsToInclude.find((s) => String(s.code) === String(sectionCode))?.reportEnabled);
    const getSectionQuestionsToInclude = (sectionCode) => sectionsToInclude.find((s) => String(s.code) === String(sectionCode))?.questionsToInclude || [];
    const isColumnEnabled = (sectionCode, column) => Boolean(getSectionQuestionsToInclude(sectionCode).find((questionToInclude) => matchesColumn(questionToInclude, column))?.reportEnabled);
    const isPrintRowPerSelectedItemEnabled = (sectionCode, column) => Boolean(getSectionQuestionsToInclude(sectionCode).find((questionToInclude) => matchesColumn(questionToInclude, column))?.columnToRow);
    const hasOtherPrintRowPerSelectedItemEnabled = (sectionCode, column) => sectionsToInclude.some((section) =>
        (section.questionsToInclude || []).some((questionToInclude) =>
            questionToInclude.questionType === "MULTI_SEL"
            && questionToInclude.columnToRow
            && !(String(section.code) === String(sectionCode) && matchesColumn(questionToInclude, column))
        )
    );
    const getEnabledColumnCount = (sectionCode) => getSectionQuestionsToInclude(sectionCode).filter((questionToInclude) => questionToInclude.reportEnabled).length;
    const selectedSignatureSectionCode = sectionsToInclude.find((section) => section.signatureSectionEnabled)?.code ?? "";

    const reportSections = (payloadData?.sections || [])
        .filter((section) => section?.code && section.type !== SURVEYPART_SECTION_TYPES.RESPONSES)
        .sort((a, b) => {
            const aIsPart = a.type === SURVEYPART_SECTION_TYPES.PART ? 1 : 0;
            const bIsPart = b.type === SURVEYPART_SECTION_TYPES.PART ? 1 : 0;
            if (aIsPart !== bIsPart) return aIsPart - bIsPart;
            return (a.showOrder) - (b.showOrder);
        });

    const signatureEligibleSections = reportSections.filter((section) => section.type !== SURVEYPART_SECTION_TYPES.PART && !section.isDefault);

    const updateReportConfiguration = (updater) => {
        setPayloadData((prevState) => {
            const currentConfig = {
                ...reportConfig,
                ...(prevState.reportConfiguration || {}),
            };
            return {
                ...prevState,
                reportConfiguration: updater(currentConfig),
            };
        });
    };

    const onEnableReportGenerationChange = (e) => {
        const checked = e?.target?.checked || false;
        updateReportConfiguration((currentConfig) => ({
            ...currentConfig,
            enableReportGeneration: checked,
        }));
    };

    const onSectionReportToggle = (sectionCode, checked) => {
        updateReportConfiguration((currentConfig) => ({
        ...currentConfig,
        sectionsToInclude: (currentConfig.sectionsToInclude || []).map((s) =>
            String(s.code) === String(sectionCode)
                ? { ...s, reportEnabled: checked }
                : s
        ),
    }));
    };

    const onSectionColumnToggle = (sectionCode, column, checked) => {
        updateReportConfiguration((currentConfig) => ({
            ...currentConfig,
            sectionsToInclude: (currentConfig.sectionsToInclude || []).map((s) =>
                String(s.code) !== String(sectionCode)
                    ? s
                    : {
                        ...s,
                        questionsToInclude: (s.questionsToInclude || []).map((questionToInclude) =>
                            matchesColumn(questionToInclude, column) ? { ...questionToInclude, reportEnabled: checked } : questionToInclude
                        ),
                    }
            ),
        }));
    };

    const onPrintRowPerSelectedItemToggle = (sectionCode, column, checked) => {
        updateReportConfiguration((currentConfig) => ({
            ...currentConfig,
            sectionsToInclude: (currentConfig.sectionsToInclude || []).map((section) => ({
                ...section,
                questionsToInclude: (section.questionsToInclude || []).map((questionToInclude) => {
                    const isTarget = String(section.code) === String(sectionCode) && matchesColumn(questionToInclude, column);
                    if (isTarget) return { ...questionToInclude, columnToRow: checked };
                    if (checked && questionToInclude.questionType === "MULTI_SEL") {
                        return { ...questionToInclude, columnToRow: false };
                    }
                    return questionToInclude;
                }),
            })),
        }));
    };

    const onSectionColumnsSelectAll = (sectionCode, checked) => {
        updateReportConfiguration((currentConfig) => ({
            ...currentConfig,
            sectionsToInclude: (currentConfig.sectionsToInclude || []).map((s) =>
                String(s.code) !== String(sectionCode)
                    ? s
                    : {
                        ...s,
                        questionsToInclude: (s.questionsToInclude || []).map((questionToInclude) => ({
                            ...questionToInclude,
                            reportEnabled: checked,
                        })),
                    }
            ),
        }));
    };

    const onSignatureSectionSelect = (sectionCode) => {
        updateReportConfiguration((currentConfig) => ({
            ...currentConfig,
            sectionsToInclude: (currentConfig.sectionsToInclude || []).map((section) => ({
                ...section,
                signatureSectionEnabled: String(section.code) === String(sectionCode),
            })),
        }));
    };

    const onQuestionFilterToggle = (filterKey, checked) => {
        updateReportConfiguration((currentConfig) => ({
            ...currentConfig,
            questionFilters: currentConfig.questionFilters.map((filter) =>
                filter.key === filterKey ? { ...filter, enabled: checked } : filter
            ),
        }));
    };

    const onQuestionFilterConditionChange = (filterKey, selectedOperatorCode) => {
        updateReportConfiguration((currentConfig) => ({
            ...currentConfig,
            questionFilters: currentConfig.questionFilters.map((filter) => {
                if (filter.key !== filterKey) return filter;
                const filterQuestionType = resolveFilterQuestionType(filter);
                console.log("filtersCatalogMap", filtersCatalogMap);
                console.log("filterQuestionType", filterQuestionType);
                const filterOptions = filtersCatalogMap.get(filterQuestionType) || [];
                const selectedOperator = filterOptions.find((option) => option.operatorCode === selectedOperatorCode);
                return {
                    ...filter,
                    filterQuestionType,
                    operatorCode: selectedOperatorCode,
                    operatorId: selectedOperator?.id || null,
                };
            }),
        }));
    };

    const onQuestionFilterValueChange = (filterKey, rawValue) => {
        updateReportConfiguration((currentConfig) => ({
            ...currentConfig,
            questionFilters: currentConfig.questionFilters.map((filter) => {
                if (filter.key !== filterKey) return filter;
                const filterQuestionType = resolveFilterQuestionType(filter);
                if (filterQuestionType === "SELECT") {
                    return {
                        ...filter,
                        filterQuestionType,
                        questionType: filterQuestionType,
                        value: Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : []),
                    };
                }

                const value = filterQuestionType === "CHECK"
                    ? String(rawValue || "")
                    : rawValue;

                return {
                    ...filter,
                    filterQuestionType,
                    questionType: filterQuestionType,
                    value,
                };
            }),
        }));
    };

    const fnValidate = (publishHandler) => {
        const validation = validateReportConfiguration(payloadData);
        if (validation.show) {
            setReportConfigurationError(validation);
            return;
        }
        
        setReportConfigurationError({ show: false, message: "" });
        setLoader({ isLoading: true, message: SAVING_DATA });
        publishHandler(payloadData);
    };

    const getFilterOptions = (questionFilter) => {
        const filterQuestionType = resolveFilterQuestionType(questionFilter);
        return filtersCatalogMap.get(filterQuestionType) || [];
    };

    const getSelectionOptionsForFilter = (questionFilter) => {
        const partQuestions = (payloadData?.sections || [])
            .filter((section) => section.type === SURVEYPART_SECTION_TYPES.PART)
            .flatMap((section) => section.questions || []);

        const question = partQuestions.find((partQuestion) => partQuestion.code === questionFilter.key);
        if (question?.selections?.length > 0) {
            return question.selections.map((selection, index) => buildSelectionOption(question.code, selection, index));
        }

        return (questionFilter.selectionOptions || []).map((option, index) => ({
            id: String(option.id || option.code || option.value || option.description || `${questionFilter.key}_${index}`),
            code: option.code || String(option.value ?? ""),
            value: String(option.value ?? ""),
            description: option.description || String(option.value || ""),
        }));
    };

    const groupedQuestionFilters = reportConfig.questionFilters.reduce((groups, filter) => {
        const groupKey = filter.sectionCode || filter.sectionDescription || "_part_";
        let group = groups.find((g) => g.key === groupKey);
        if (!group){
            group = {
                key: groupKey,
                title: filter.sectionDescription || "Part Information",
                showOrder: filter.sectionShowOrder ?? 0,
                filters: [],
            };
            groups.push(group);
        }
        group.filters.push(filter);
        return groups;
    }, []).sort((a, b) => (a.showOrder ?? 0) - (b.showOrder ?? 0));

    const getSectionFilters = (section) =>
            groupedQuestionFilters.find((group) => group.key === (section.code || section.description || "_part_"))?.filters || [];

    const toggleSectionFilters = (sectionKey) => {
        if(!isSectionReportEnabled(sectionKey)) return;
        setExpandedSections((prev) => ({...prev, [sectionKey]: !prev[sectionKey]}));
    };

    const onShowPreview = () => {
        const validation = validateReportConfiguration(payloadData);
        if(validation.show){
            setReportConfigurationError(validation);
            return;
        }
        setReportConfigurationError({ show: false, message: "" });
        
        if(previewUrl){
            window.URL.revokeObjectURL(previewUrl);
            setPreviewUrl(undefined);
        }
        setPreviewError("");
        setPreviewLoading(true);
        setShowPreviewModal(true);

        const previewPayload = {
            enableReportGeneration: Boolean(reportConfig.enableReportGeneration),
            includeSignatureSection: Boolean(reportConfig.includeSignatureSection),
            includeDisclaimerSection: Boolean(reportConfig.includeDisclaimerSection),
            disclaimerMessage: reportConfig.disclaimerMessage ?? "",
            sectionsToInclude: (reportConfig.sectionsToInclude || []).map((s) => ({
                ...(s.id != null ? { id: s.id } : {}),
                ...(s.sectionId != null ? { sectionId: s.sectionId } : {}),
                code: s.code,
                description: s.description,
                type: s.type,
                active: Boolean(s.active),
                reportEnabled: Boolean(s.reportEnabled),
                signatureSectionEnabled: Boolean(s.signatureSectionEnabled),
                ...(s.questionsToInclude ? { questionsToInclude: s.questionsToInclude.map((questionToInclude) => ({
                    ...(questionToInclude.id != null ? { id: questionToInclude.id } : {}),
                    ...(questionToInclude.columnMapping ? { columnMapping: questionToInclude.columnMapping } : {
                        ...(questionToInclude.questionId != null ? { questionId: questionToInclude.questionId } : {}),
                        questionCode: questionToInclude.questionCode,
                        questionText: questionToInclude.questionText,
                        questionType: questionToInclude.questionType,
                    }),
                    reportEnabled: Boolean(questionToInclude.reportEnabled),
                    ...(questionToInclude.questionType === "MULTI_SEL" ? { columnToRow: Boolean(questionToInclude.columnToRow) } : {}),
                }))} : {}),
            })),
            surveyId: payloadData?.id,
        };

        getReportConfigurationPreview(previewPayload, authState?.accessToken?.accessToken)
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(response.statusText);
                }
                const blob = await response.blob();
                setPreviewUrl(window.URL.createObjectURL(blob));
            })
            .catch((error) => {
                console.log(error);
                setPreviewError("Failed to generate preview. Please try again later.");
            })
            .finally(() => setPreviewLoading(false));
    };

    const onClosePreview = () => {
        setShowPreviewModal(false);
        if(previewUrl){
            window.URL.revokeObjectURL(previewUrl);
            setPreviewUrl(undefined);
        }
        setPreviewError("");
    };

    const renderValueInput = (questionFilter) => {
        const filterQuestionType = resolveFilterQuestionType(questionFilter);
        const disabled = !questionFilter.enabled;

        if (filterQuestionType === "CHECK") {
            const currentValue =
                String(questionFilter.value ?? "");
            return (
                <SelectField
                    label=""
                    value={currentValue}
                    onChange={(e) => onQuestionFilterValueChange(questionFilter.key, e.target.value)}
                    disabled={disabled}
                    sx={{ "& .MuiFormLabel-root": { fontSize: "12px" } }}
                >
                    <Option disabled hidden value="">Select</Option>
                    <Option value="true">True</Option>
                    <Option value="false">False</Option>
                </SelectField>
            );
        }

        if (filterQuestionType === "SELECT") {
            const selectionOptions = getSelectionOptionsForFilter(questionFilter);
            const selectedValues = Array.isArray(questionFilter.value) ? questionFilter.value : (questionFilter.value ? [String(questionFilter.value)] : []);
            return (
                <SelectField
                    label=""
                    multiple
                    value={selectedValues}
                    renderValue={(selected) => (selected?.length ? selected.join(", ") : "Select")}
                    onChange={(e) => onQuestionFilterValueChange(questionFilter.key, e.target.value)}
                    disabled={disabled || selectionOptions.length === 0}
                    sx={{ "& .MuiFormLabel-root": { fontSize: "12px" } }}
                >
                    {selectionOptions.length === 0 && <Option disabled value="">No values available</Option>}
                    {selectionOptions.map((option) => (
                        <Option key={option.id} value={option.description}>
                            <Checkbox checked={selectedValues.includes(option.description)} />
                            {option.description}
                        </Option>
                    ))}
                </SelectField>
            );
        }

        if (filterQuestionType === "OPEN" || filterQuestionType === "NUM") {
            return (
                <TextField
                    size="small"
                    type={filterQuestionType === "NUM" ? "number" : "text"}
                    value={questionFilter.value ?? ""}
                    onChange={(e) => onQuestionFilterValueChange(questionFilter.key, e.target.value)}
                    disabled={disabled}
                    placeholder="Enter value"
                    sx={{ "& .MuiInputBase-input": { fontSize: "12px" } }}
                />
            );
        }

        return (
            <Typography variant="subtitle" sx={{ color: "#9ca3af" }}>
                -
            </Typography>
        );
    };

    return (
        <Card>
            <CardContent>
                {loader.message !== "" && <SelfServiceLoader loader={loader} />}
                {loader.message === "" && (
                <>
                <Grid item>
                    <Typography variant="h1" mb={4}>
                    {currentStep.step} - {currentStep.title}
                    </Typography>
                    <Typography variant="subtitle">
                        Configure the sections and data to include when generating a report from survey responses.
                    </Typography>
                </Grid>
                {reportConfigurationError?.show && (
                    <Grid item mt={2} mb={2}>
                        <Grid sx={{ backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "6px", padding: "12px 16px" }}>
                            <Typography variant="subtitle" sx={{ color: "#856404" }}>
                                {reportConfigurationError.message}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                <Grid item mt={4} pt={4} sx={{ borderTop: "1px solid #d9d9d9" }}>
                    <Grid
                        container
                        sx={{
                            border: "1px solid #d9d9d9f5",
                            borderRadius: "6px",
                            padding: "24px 28px",
                        }}
                    >
                        <Grid item xs={12}>
                            <Grid container alignItems="flex-start" columnSpacing={2}>
                                <Grid item sx={{ pt: "2px" }}>
                                    <Checkbox
                                        checked={Boolean(reportConfig.enableReportGeneration)}
                                        onChange={onEnableReportGenerationChange}
                                    />
                                </Grid>
                                <Grid item xs>
                                    <Grid container direction="column" alignItems="flex-start">
                                        <Grid item>
                                            <Typography variant="h4" sx={{ fontSize: "1rem", lineHeight: 1.2, fontWeight: 700 }}>
                                                Enable Report Generation
                                            </Typography>
                                        </Grid>
                                        <Grid item mt={1}>
                                            <Typography variant="subtitle" sx={{ fontSize: "0.875rem", lineHeight: 1.45 }}>
                                                When enabled, a PDF report can be generated from survey responses using the configuration below.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {reportConfig.enableReportGeneration && (
                    <Grid item mt={5} mb={6}>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            Sections to Include
                        </Typography>
                        <Grid container justifyContent = "flex-end" mt = {2}>
                            <Link href = "#" onClick = {(e) => { e.preventDefault(); onShowPreview(); }} sx={previewLoading ? { pointerEvents: "none", opacity: 0.5  } : {}}>Show Preview</Link>
                        </Grid>
                        <Typography variant="subtitle" mt={1} mb={2} sx={{ fontSize: "0.875rem", lineHeight: 1.4 }}>
                            Select which survey sections should appear in the generated report.
                        </Typography>

                        <Grid mt={2} sx={{ border: "1px solid #d9d9d9", borderRadius: "6px", overflow: "hidden" }}>
                            {reportSections.map((section) => {
                                const sectionKey = section.code;

                                if (section.type === SURVEYPART_SECTION_TYPES.SUPPLIER) {
                                    return (
                                        <Grid key={sectionKey} container alignItems="center" sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e5e5e5" }}>
                                            <Grid item xs={10}>
                                                <Grid container alignItems="center" wrap="nowrap" columnSpacing={2}>
                                                    <Grid item>
                                                        <Checkbox
                                                            checked={isSectionReportEnabled(section.code)}
                                                            onChange={(e) => onSectionReportToggle(section.code, e?.target?.checked || false)}
                                                        />
                                                    </Grid>
                                                    <Grid item xs zeroMinWidth>
                                                        <Typography variant="h4" sx={{ wordBreak: "break-word" }}>{section.description || DEFAULT_PART_SECTION_TITLES.SUPP_INFO} <span style={{ color: "#9ca3af" }}>(Default)</span></Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    );
                                }

                                if (section.type === SURVEYPART_SECTION_TYPES.QUESTIONS) {
                                    return (
                                        <Grid key={sectionKey} container alignItems="center" sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e5e5e5" }}>
                                            <Grid item xs={10}>
                                                <Grid container alignItems="center" wrap="nowrap" columnSpacing={2}>
                                                    <Grid item>
                                                        <Checkbox
                                                            checked={isSectionReportEnabled(section.code)}
                                                            onChange={(e) => onSectionReportToggle(section.code, e?.target?.checked || false)}
                                                        />
                                                    </Grid>
                                                    <Grid item xs zeroMinWidth>
                                                        <Typography variant="h4" sx={{ wordBreak: "break-word" }}>{section.description || DEFAULT_PART_SECTION_TITLES.AUTH_INFO}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    );
                                }

                                const sectionFilters = getSectionFilters(section);
                                const isExpanded = Boolean(expandedSections[sectionKey]);
                                const sectionColumns = buildSectionColumns(section);
                                const enabledColumnCount = getEnabledColumnCount(section.code);
                                const areAllColumnsSelected = sectionColumns.length > 0 && enabledColumnCount === sectionColumns.length;
                                const activeFilterCount = sectionFilters.filter((filter) => filter.enabled).length;

                                return (
                                    <Grid key={sectionKey}>
                                        <Grid container alignItems="center" sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e5e5e5" }}>
                                            <Grid item xs={8}>
                                                <Grid container alignItems="center" wrap="nowrap" columnSpacing={2}>
                                                    <Grid item>
                                                        <Checkbox
                                                            checked={isSectionReportEnabled(section.code)}
                                                            onChange={(e) => onSectionReportToggle(section.code, e?.target?.checked || false)}
                                                        />
                                                    </Grid>
                                                    <Grid item xs zeroMinWidth>
                                                        <Typography variant="h4" sx={{ wordBreak: "break-word" }}>{section.description || DEFAULT_PART_SECTION_TITLES.PART_INFO} <span style={{ color: "#9ca3af" }}>(Default)</span></Typography>
                                                        {isSectionReportEnabled(section.code) && (
                                                            <Typography variant="subtitle" sx={{ color: "#9ca3af", fontSize: "0.8rem" }}>
                                                                {enabledColumnCount} of {sectionColumns.length} questions{activeFilterCount > 0 ? ` · ${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""}` : ""}
                                                            </Typography>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={4} textAlign="right">
                                                <Typography
                                                    variant="subtitle"
                                                    sx={{
                                                        color: isSectionReportEnabled(section.code) ? "#2e7d32" : "#9ca3af",
                                                        cursor: isSectionReportEnabled(section.code) ? "pointer" : "not-allowed",
                                                        fontWeight: 520,
                                                    }}
                                                    onClick={() => toggleSectionFilters(sectionKey)}
                                                >
                                                    Configure {isExpanded ? "v" : ">"}
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        {isExpanded && isSectionReportEnabled(section.code) && (
                                            <Grid sx={{ backgroundColor: "#fafafa", borderTop: "1px solid #e5e5e5" }}>
                                                <Grid sx={{ px: 2, py: 2, borderBottom: "1px solid #e5e5e5" }}>
                                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                        Questions to Include
                                                    </Typography>
                                                    <Typography variant="subtitle" mt={0.5}>
                                                        Select which questions are shown for each part in the report. <strong>Part Number</strong> and <strong>Description</strong> are always included.
                                                    </Typography>
                                                </Grid>

                                                {sectionColumns.length > 0 && enabledColumnCount === 0 && (
                                                    <Grid sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e5e5e5", backgroundColor: "#fff3cd" }}>
                                                        <Typography variant="subtitle" sx={{ color: "#856404" }}>
                                                            At least one question must be selected for this section.
                                                        </Typography>
                                                    </Grid>
                                                )}

                                                {sectionColumns.length === 0 ? (
                                                    <Grid sx={{ px: 2, py: 1.5 }}>
                                                        <Typography variant="subtitle" sx={{ color: "#9ca3af" }}>No configurable questions in this section.</Typography>
                                                    </Grid>
                                                ) : (
                                                    <>
                                                        <Grid container alignItems="center" columnSpacing={2} sx={{ px: 2, py: 1, borderBottom: "1px solid #e5e5e5" }}>
                                                            <Grid item>
                                                                <Checkbox
                                                                    checked={areAllColumnsSelected}
                                                                    indeterminate={enabledColumnCount > 0 && !areAllColumnsSelected}
                                                                    onChange={(e) => onSectionColumnsSelectAll(section.code, e?.target?.checked || false)}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                <Typography variant="label">Select All ({enabledColumnCount}/{sectionColumns.length})</Typography>
                                                            </Grid>
                                                        </Grid>
                                                        {sectionColumns.map((column) => (
                                                            <Grid key={column.key} container alignItems="center" columnSpacing={2} sx={{ px: 2, py: 1, borderBottom: "1px solid #ececec" }}>
                                                                <Grid item>
                                                                    <Checkbox
                                                                        checked={isColumnEnabled(section.code, column)}
                                                                        onChange={(e) => onSectionColumnToggle(section.code, column, e?.target?.checked || false)}
                                                                    />
                                                                </Grid>
                                                                <Grid item>
                                                                    <Typography variant="h4">{column.label}</Typography>
                                                                    <Typography variant="subtitle" sx={{ color: "#9ca3af" }}>
                                                                        {column.columnMapping ? "Additional Column" : (questionTypeLabelMap[column.questionType] || column.questionType)}
                                                                    </Typography>
                                                                    {!column.columnMapping && column.questionType === "MULTI_SEL" && (
                                                                        <Grid container alignItems="center" wrap="nowrap" sx={{ mt: 1, px: 1, py: 0.5, border: "1px solid #c8e6c9", borderRadius: "4px", backgroundColor: "#f5faf5" }}>
                                                                            <Grid item>
                                                                                <Checkbox
                                                                                    size="small"
                                                                                    checked={isPrintRowPerSelectedItemEnabled(section.code, column)}
                                                                                    disabled={hasOtherPrintRowPerSelectedItemEnabled(section.code, column)}
                                                                                    onChange={(e) => onPrintRowPerSelectedItemToggle(section.code, column, e?.target?.checked || false)}
                                                                                />
                                                                            </Grid>
                                                                            <Grid item>
                                                                                <Typography variant="label">Print a row per selected item</Typography>
                                                                                <Typography variant="subtitle" sx={{ color: "#9ca3af", fontSize: "0.8rem" }}>
                                                                                    Each selected answer will appear on its own report row.
                                                                                </Typography>
                                                                            </Grid>
                                                                        </Grid>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        ))}
                                                    </>
                                                )}
                                            </Grid>
                                        )}

                                        {isExpanded && isSectionReportEnabled(section.code) && (
                                            <Grid sx={{ backgroundColor: "#fafafa", borderTop: "1px solid #e5e5e5" }}>
                                                <Grid sx={{ px: 2, py: 2, borderBottom: "1px solid #e5e5e5" }}>
                                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                        Question Filters
                                                    </Typography>
                                                    <Typography variant="subtitle" mt={0.5}>
                                                        Enable a filter on any question to limit which parts appear in the report. Multiple options on the same question match any of them; filters on different questions are combined with AND logic.
                                                    </Typography>
                                                </Grid>

                                                <Grid sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e5e5e5", backgroundColor: "#fff8e1" }}>
                                                    <Grid container alignItems="flex-start" columnSpacing={1}>
                                                        <Grid item sx={{ pt: "2px" }}>
                                                            <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "#e65100" }} />
                                                        </Grid>
                                                        <Grid item xs>
                                                            <Typography variant="subtitle" sx={{ fontSize: "0.8rem", color: "#e65100" }}>
                                                                <strong>Awareness:</strong> Questions marked with{" "}
                                                                <Chip
                                                                    icon={<FilterAltIcon sx={{ fontSize: "0.75rem !important" }} />}
                                                                    label="Filterable"
                                                                    size="small"
                                                                    sx={{ backgroundColor: "#1565c0", color: "#fff", height: "18px", fontSize: "0.7rem", "& .MuiChip-icon": { color: "#fff" } }}
                                                                />{" "}
                                                                are eligible for filtering. Only the question types{" "}
                                                                <strong>Open Text, Numerical, Option and Checkbox</strong> support filter conditions.
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>

                                                <Grid container sx={{ px: 2, py: 1, borderBottom: "1px solid #e5e5e5" }}>
                                                    <Grid item xs={6}><Typography variant="label">Question</Typography></Grid>
                                                    <Grid item xs={3}><Typography variant="label">Condition</Typography></Grid>
                                                    <Grid item xs={3}><Typography variant="label">Value</Typography></Grid>
                                                </Grid>

                                                {sectionFilters.length === 0 && (
                                                    <Grid sx={{ px: 2, py: 1.5, borderBottom: "1px solid #ececec" }}>
                                                        <Typography variant="subtitle" sx={{ color: "#9ca3af" }}>No filterable questions in this section.</Typography>
                                                    </Grid>
                                                )}

                                                {sectionFilters.map((filter) => (
                                                    <Grid key={filter.key} container alignItems="center" columnSpacing={2} sx={{ px: 2, py: 1.5, borderBottom: "1px solid #ececec" }}>
                                                        <Grid item xs={6}>
                                                            <Grid container alignItems="center" columnSpacing={2}>
                                                                <Grid item>
                                                                    <Checkbox
                                                                        checked={Boolean(filter.enabled)}
                                                                        onChange={(e) => onQuestionFilterToggle(filter.key, e?.target?.checked || false)}
                                                                    />
                                                                </Grid>
                                                                <Grid item>
                                                                    <Typography variant="h4">{filter.label}</Typography>
                                                                    <Grid container alignItems="center" columnSpacing={0}>
                                                                        <Grid item>
                                                                            <Typography variant="subtitle" sx={{ color: "#9ca3af" }}>{filter.type}</Typography>
                                                                        </Grid>
                                                                        <Grid item sx={{ ml: 1.5 }}>
                                                                            <Chip
                                                                                icon={<FilterAltIcon sx={{ fontSize: "0.75rem !important" }} />}
                                                                                label="Filterable"
                                                                                size="small"
                                                                                sx={{ backgroundColor: "#1565c0", color: "#fff", height: "18px", fontSize: "0.7rem", "& .MuiChip-icon": { color: "#fff" } }}
                                                                            />
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid item xs={3} sx={{ pr: 1 }}>
                                                            <SelectField
                                                                label=""
                                                                value={filter.operatorCode || ""}
                                                                onChange={(e) => onQuestionFilterConditionChange(filter.key, e.target.value)}
                                                                disabled={!filter.enabled || getFilterOptions(filter).length === 0}
                                                                sx={{ "& .MuiFormLabel-root": { fontSize: "12px" } }}
                                                            >
                                                                <Option disabled hidden value="">
                                                                    {getFilterOptions(filter).length === 0 ? "No filters available" : "Select"}
                                                                </Option>
                                                                {getFilterOptions(filter).map((option) => (
                                                                    <Option key={option.id} value={option.operatorCode}>
                                                                        {resolveFilterQuestionType(filter) === "SELECT"
                                                                            ? (OPTION_OPERATOR_LABELS[option.operatorCode] || option.description)
                                                                            : option.description}
                                                                    </Option>
                                                                ))}
                                                            </SelectField>
                                                        </Grid>
                                                        <Grid item xs={3} sx={{ pl: 1 }}>
                                                            {renderValueInput(filter)}
                                                        </Grid>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        )}
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Grid>
                )}

                {reportConfig.enableReportGeneration && (
                    <Grid item mt={5} mb={6}>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            Additional Report Elements
                        </Typography>
                        <Typography variant="subtitle" mt={1} mb={2} sx={{ fontSize: "0.875rem", lineHeight: 1.4 }}>
                            Append optional pages or blocks at the end of the generated report.
                        </Typography>

                        <Grid mt={2} sx={{ border: "1px solid #d9d9d9", borderRadius: "6px", overflow: "hidden" }}>
                            <Grid container alignItems="flex-start" sx={{ px: 2, py: 1.5, borderBottom: "1px solid #ececec" }}>
                                <Grid item xs={12}>
                                    <Grid container alignItems="flex-start" columnSpacing={2}>
                                        <Grid item>
                                            <Checkbox
                                                checked={Boolean(reportConfig.includeSignatureSection)}
                                                onChange={(e) => onAdditionalElementToggle("includeSignatureSection", e?.target?.checked || false)}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            <Typography variant="h4">Signature Section</Typography>
                                            <Typography variant="subtitle" mt={0.5}>
                                                Adds a signature block at the end of the report. The fields rendered in this section correspond to the fields configured in the section selected below.
                                            </Typography>
                                            {reportConfig.includeSignatureSection && (
                                                <Grid mt={2} sx={{ border: "1px solid #e5e5e5", borderRadius: "6px", overflow: "hidden" }}>
                                                    <Grid sx={{ px: 2, py: 1, backgroundColor: "#fafafa", borderBottom: "1px solid #e5e5e5" }}>
                                                        <Typography variant="subtitle" sx={{ fontSize: "0.8rem" }}>
                                                            Select the section that provides the values shown in the signature block.
                                                        </Typography>
                                                    </Grid>
                                                    {signatureEligibleSections.length === 0 ? (
                                                        <Grid sx={{ px: 2, py: 1 }}>
                                                            <Typography variant="subtitle" sx={{ color: "#9ca3af" }}>No eligible sections available.</Typography>
                                                        </Grid>
                                                    ) : (
                                                        <RadioGroup
                                                            value={selectedSignatureSectionCode}
                                                            onChange={(e) => onSignatureSectionSelect(e.target.value)}
                                                            sx={{ px: 2, py: 1 }}
                                                        >
                                                            {signatureEligibleSections.map((section) => (
                                                                <FormControlLabel
                                                                    key={section.code}
                                                                    value={String(section.code)}
                                                                    control={<Radio size="small" />}
                                                                    label={<Typography variant="h4">{section.description || section.code}</Typography>}
                                                                />
                                                            ))}
                                                        </RadioGroup>
                                                    )}
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid container alignItems="flex-start" sx={{ px: 2, py: 1.5 }}>
                                <Grid item xs={12}>
                                    <Grid container alignItems="flex-start" columnSpacing={2}>
                                        <Grid item>
                                            <Checkbox
                                                checked={Boolean(reportConfig.includeDisclaimerSection)}
                                                onChange={(e) => onAdditionalElementToggle("includeDisclaimerSection", e?.target?.checked || false)}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            <Typography variant="h4">Disclaimer / Agreement</Typography>
                                            <Typography variant="subtitle" mt={0.5}>
                                               Disclaimer or agreement text that will appear at the end of the report. This text can be used to provide legal disclaimers, terms of use, or any other relevant information that should be communicated to the report recipients.
                                            </Typography>
                                            {reportConfig.includeDisclaimerSection && (
                                                <Grid mt = {2}>
                                                    <TextField
                                                        label="Disclaimer / Agreement Text"
                                                        placeholder= "Enter disclaimer message here..."
                                                        value={reportConfig.disclaimerMessage || ""}
                                                        onChange={(e) => onDisclaimerMessageChange(e.target.value)}
                                                        multiline
                                                        minRows={3}
                                                        fullWidth
                                                        inputProps={{ maxLength: 1000 }}
                                                        helperText={`${(reportConfig.disclaimerMessage || "").length}/1000 characters`}
                                                        sx={{ "& .MuiInputBase-input": { fontSize: "12px" } }}
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}

                <Grid container justifyContent="flex-end" mt={4}>
                    <SelfServiceButtons
                        activeStep={activeStep}
                        setActiveStep={setActiveStep}
                        fnValidate={fnValidate}
                        payloadData={payloadData}
                        setPayloadData={setPayloadData}
                        setLoader={setLoader}
                    />
                </Grid>
                </>
                )}
            </CardContent>
            <GenericFuelModal
                title={currentStep.title}
                message={
                    previewLoading
                        ? (
                            <Grid container direction="column" alignItems="center" justifyContent="center" rowSpacing={2} sx={{ minHeight: "40vh" }}>
                                <Grid item><CircularProgress size="lg" /></Grid>
                                <Grid item><Typography variant="subtitle">Generating preview...</Typography></Grid>
                            </Grid>
                        )
                        : previewError
                            ? (
                                <Grid container direction="column" alignItems="center" justifyContent="center" rowSpacing={2} sx={{ minHeight: "40vh" }}>
                                    <Grid item><Typography variant="subtitle" color="#C21020">{previewError}</Typography></Grid>
                                    <Grid item><Button variant="primary" onClick={onShowPreview}>Retry</Button></Grid>
                                </Grid>
                            )
                            : previewUrl
                                ? <iframe
                                    title="Report Preview"
                                    src={`${previewUrl}#zoom=page-width`}
                                    style={{ width: "100%", height: "85vh", border: "none" }}
                                />
                                : <></>
                }
                show={showPreviewModal}
                handleClose={onClosePreview}
                handleSave={onClosePreview}
                customStyle={{ "& .MuiPaper-root": { maxWidth: "95vw", width: "95vw" },
                            "& .MuiDialogContent-root": { padding: "0" },}}
            />
        </Card>
      );
}; 

export default ReportConfigurationStep;
